import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IdentitySection } from "./sections/IdentitySection";
import { GenderSection } from "./sections/GenderSection";
import { FitProfileSection } from "./sections/FitProfileSection";
import { ContactSection } from "./sections/ContactSection";
import { TryOnPhotosSection } from "./sections/TryOnPhotosSection";
import { SettingsFooter } from "../components";
import { useSettingsFormState } from "../store";
import { ProfileSkeleton } from "./components/ProfileSkeleton";
import { useProfile } from "./hooks/use-profile";

export const ProfileTab: React.FC = () => {
  const { t } = useTranslation();
  const { registerDraftReset, unregisterDraftReset } =
    useSettingsFormState("profile");
  const {
    user,
    updateProfile,
    hasUnsavedChanges,
    isTabLoading,
    isUpdating,
    isFormReady,
    resetDraft,
  } = useProfile();

  useEffect(() => {
    registerDraftReset(resetDraft);
    return () => unregisterDraftReset(resetDraft);
  }, [registerDraftReset, unregisterDraftReset, resetDraft]);

  const handleSave = async () => {
    if (!user) return;
    await updateProfile();
  };

  const handleCancel = () => {
    resetDraft();
  };

  if (!isFormReady) {
    return <ProfileSkeleton />;
  }

  return (
    <div className="flex flex-col gap-5 md:gap-8">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:gap-5 pb-4 md:pb-5 border-b border-border-secondary dark:border-gray-600">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100 leading-7">
            {t("settings.profile.title")}
          </h2>
          <p className="text-sm text-text-tertiary dark:text-gray-400 leading-5 max-w-2xl">
            {t("settings.profile.description")}
          </p>
        </div>
      </div>

      <IdentitySection />
      <GenderSection />
      <FitProfileSection />
      <TryOnPhotosSection />
      <ContactSection />

      <SettingsFooter
        onSave={handleSave}
        onCancel={handleCancel}
        isLoading={isTabLoading || isUpdating}
        disabled={!hasUnsavedChanges}
      />
    </div>
  );
};
